import styles from './RelatedApps.module.css';

/**
 * `RelatedApps` — the "you just finished doing X — here are 1–2
 * sibling apps that fit what you were just exploring" block. Drop it
 * at the bottom of any completion / result / reflection surface.
 *
 * Each playground app deploys at its own subdomain
 * (`<slug>.hypneuronerd.ing`). `urlFor` builds the absolute URL so
 * apps don't have to know their own subdomain map. `APP_LABELS` is
 * the canonical display name — keep it in sync with the shell's
 * gallery in `apps/shell/src/data/apps.ts`.
 *
 * The default suggestion table `RELATED_BY_APP` is keyed by the
 * *source* app slug. Each app's completion screen passes its own
 * slug in (or passes a custom `apps` prop for per-result-state
 * suggestions). The atlas has its own per-entity table; the rest of
 * the playground uses the static defaults here.
 */

export interface RelatedApp {
  /** Target app slug — must match a subdomain. */
  slug: string;
  /** One short line explaining why this app fits the current moment. */
  reason: string;
}

export interface RelatedAppsProps {
  /** Suggestions to render. Caller can pass `RELATED_BY_APP[source]`
   * for the static default, or build a custom array per result state.
   * Accepts `undefined` so callers don't have to non-null-assert the
   * index access. */
  apps: RelatedApp[] | undefined;
  /** Section heading. Default: "try another app". */
  heading?: string;
  /** Visual variant — `cream` (light surface) vs `midnight` (dark surface). */
  surface?: 'cream' | 'midnight';
  /** Optional className passthrough for app-level overrides. */
  className?: string;
}

export const APP_LABELS: Record<string, string> = {
  'archetype-mirror': 'the archetype mirror',
  'card-sort': 'the card sort',
  'dose-calibrator': 'the dose calibrator',
  'ritual-forge': 'the ritual forge',
  'negotiation-letter': 'the negotiation letter',
  'aftercare-recipe': 'the aftercare recipe',
  candle: 'the candle',
  'daily-word': 'the daily word',
  'red-flag-green-flag': 'red flag, green flag',
  'negotiation-sim': 'the negotiation sim',
  'glossary-match': 'glossary match',
  atlas: 'the atlas',
  trance: 'the trance engine',
};

export function urlFor(slug: string): string {
  return `https://${slug}.hypneuronerd.ing/`;
}

/**
 * Static suggestion table — keyed by the *source* app slug. Each
 * entry is the default "what fits next" for visitors finishing that
 * app. Apps with multiple distinct completion moments can override
 * with a custom `apps` prop.
 */
export const RELATED_BY_APP: Record<string, RelatedApp[]> = {
  'archetype-mirror': [
    { slug: 'atlas', reason: 'explore the region of the country that matches your archetype.' },
    { slug: 'ritual-forge', reason: 'build a small practice shaped to who you are becoming.' },
    { slug: 'card-sort', reason: 'sort what lives inside your archetype as yes, no, or curious.' },
  ],
  'card-sort': [
    { slug: 'negotiation-letter', reason: 'draft a letter that articulates the limits you just sorted.' },
    { slug: 'aftercare-recipe', reason: 'plan the care that suits the intensity you said yes to.' },
    { slug: 'atlas', reason: 'find the regions of practice your sort just pointed toward.' },
  ],
  'dose-calibrator': [
    { slug: 'aftercare-recipe', reason: 'a recovery plan sized to the dose you just chose.' },
    { slug: 'candle', reason: 'a contained, single-session practice that fits a lighter dose.' },
    { slug: 'ritual-forge', reason: 'compose a ritual that holds the dose you can actually carry.' },
  ],
  'ritual-forge': [
    { slug: 'candle', reason: 'let the candle hold the duration of your ritual.' },
    { slug: 'daily-word', reason: 'anchor your ritual to a single word for the day.' },
    { slug: 'archetype-mirror', reason: 'check that the ritual you composed fits the shape you make.' },
  ],
  'negotiation-letter': [
    { slug: 'negotiation-sim', reason: 'rehearse delivering what you just wrote.' },
    { slug: 'card-sort', reason: 'sort your yes / no / curious before you send it.' },
    { slug: 'red-flag-green-flag', reason: 'test your wording against the pattern-spotting drills.' },
  ],
  'aftercare-recipe': [
    { slug: 'candle', reason: 'one of the simplest aftercare practices — ten minutes of stillness.' },
    { slug: 'ritual-forge', reason: 'turn your recipe into a small ceremony you can repeat.' },
    { slug: 'daily-word', reason: 'a gentle anchor word for the days after.' },
  ],
  candle: [
    { slug: 'daily-word', reason: 'one quality to carry out of the stillness.' },
    { slug: 'ritual-forge', reason: 'formalize the sitting into a small ritual you return to.' },
    { slug: 'aftercare-recipe', reason: 'if you sat as aftercare, build the rest of the recipe.' },
  ],
  'daily-word': [
    { slug: 'ritual-forge', reason: 'build a small practice around the word you held today.' },
    { slug: 'candle', reason: 'ten minutes of sitting with the word.' },
    { slug: 'glossary-match', reason: 'see how the day’s word relates to the wider vocabulary.' },
  ],
  'red-flag-green-flag': [
    { slug: 'negotiation-sim', reason: 'practice the conversations that catch the patterns earlier.' },
    { slug: 'negotiation-letter', reason: 'draft how you would name a flag if you saw one.' },
    { slug: 'card-sort', reason: 'clarify the limits the patterns just sharpened.' },
  ],
  'negotiation-sim': [
    { slug: 'negotiation-letter', reason: 'write what you would actually say next time.' },
    { slug: 'aftercare-recipe', reason: 'plan the recovery that suits the conversation you just rehearsed.' },
    { slug: 'red-flag-green-flag', reason: 'sharpen the gut that reads the moment in real time.' },
  ],
  'glossary-match': [
    { slug: 'daily-word', reason: 'one of the matched terms, held for a day.' },
    { slug: 'atlas', reason: 'see where today’s words live on the map.' },
    { slug: 'card-sort', reason: 'apply the vocabulary to your own yes / no / curious.' },
  ],
};

export function RelatedApps({
  apps,
  heading = 'try another app',
  surface = 'midnight',
  className,
}: RelatedAppsProps) {
  if (!apps || apps.length === 0) return null;
  const surfaceClass = surface === 'cream' ? styles.surfaceCream : styles.surfaceMidnight;
  return (
    <section className={[styles.section, surfaceClass, className].filter(Boolean).join(' ')}>
      <h3 className={styles.heading}>{heading}</h3>
      <ul className={styles.list}>
        {apps.map((app) => (
          <li key={app.slug}>
            <a
              href={urlFor(app.slug)}
              className={styles.link}
              target="_self"
              rel="noopener"
            >
              <span className={styles.name}>
                {APP_LABELS[app.slug] ?? app.slug}
                <span className={styles.arrow} aria-hidden="true">→</span>
              </span>
              <span className={styles.reason}>{app.reason}</span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
